/**
 * Savanto Chatbot Loader
 *
 * Lightweight vanilla JS script (~5KB) that runs on host page.
 * Creates trigger button and manages iframe containing the widget.
 */

import type { PromptData } from '../types/streaming';
import { applyMobileStyles, createIframe, type IframeAPI } from './createIframe';
import { createPopover, type PopoverAPI } from './createPopover';
import { createTrigger, type TriggerButtonAPI } from './createTrigger';
import type { ChildToParentMessage, LoaderConfig, LoaderTheme, ParentToChildMessage, SavantoChatbotAPI } from './types';

// Widget URL - can be overridden via config
const DEFAULT_WIDGET_URL = 'https://cdn.savanto.ai/chat-widget.html';

// 480px matches the mobile-fullscreen breakpoint baked into the iframe
// container styles. Pulled out as a constant so the loader and any test
// can reference the same value rather than hard-coding the literal twice.
const MOBILE_BREAKPOINT_PX = 480;

class SavantoLoader {
  private config: LoaderConfig;
  private trigger: TriggerButtonAPI | null = null;
  private popover: PopoverAPI | null = null;
  private iframe: IframeAPI | null = null;
  private isOpen = false;
  private isReady = false;
  private isRendered = false; // Widget has received config and rendered
  private pendingOpen = false; // User wants to open but widget not ready yet
  private pendingMessages: ParentToChildMessage[] = [];
  private widgetUrl: string;
  // Tracks whether the host page has explicitly revoked analytics consent.
  // Without this, getAnalyticsConsentStatus() could never report 'denied'.
  private consentRevoked = false;
  // Set by grantAnalyticsConsent() so a prior revoke is cleared symmetrically.
  private consentExplicitlyGranted = false;
  // Mobile detection — held so `destroy()` can tear them down. The previous
  // implementation used a raw `resize` listener that fired on every pixel of
  // a window drag (60+ times/sec) and unconditionally rewrote a multi-line
  // `cssText` on a `position: fixed; z-index: 999999` container, plus an
  // anonymous `orientationchange` arrow that could never be unregistered.
  private mobileMql: MediaQueryList | null = null;
  private mobileMqlListener: ((event: MediaQueryListEvent) => void) | null = null;
  private orientationChangeListener: (() => void) | null = null;
  // Held so `destroy()` can remove the postMessage bridge and cancel the
  // trigger-reveal fallback — otherwise a re-initialized host page (Shopify
  // settings reload, SPA route change) leaks a listener per cycle and the
  // 15s timer fires against a torn-down instance.
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private revealFallbackTimer: number | null = null;
  // Server-enforced branding (free/personal tiers). Held so post-bootstrap
  // config writers can be clamped, not just the initial merge.
  private serverRequiresBranding = false;

  constructor(config: LoaderConfig) {
    this.config = config;
    this.widgetUrl = config.widgetUrl || DEFAULT_WIDGET_URL;

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  private initialize(): void {
    const isInlineMode = this.config.embedMode === 'inline';

    // Inline mode has no floating chrome: createTrigger/createPopover decide
    // that themselves and return null for inline embeds (factory-owns-the-
    // decision, same pattern as createIframe's mode handling).
    this.trigger = createTrigger(this.config, () => this.toggle());
    this.popover = createPopover(this.config, () => this.open());
    this.iframe = createIframe(this.widgetUrl, this.config, () => this.onIframeLoad());

    // Hide trigger until widget sends 'loaded'; fallback reveals it after 15s
    if (!isInlineMode) {
      this.trigger?.hide();
      this.revealFallbackTimer = window.setTimeout(() => {
        this.revealFallbackTimer = null;
        if (this.isRendered) return;
        this.isRendered = true;
        if (this.pendingOpen) {
          this.pendingOpen = false;
          this.isOpen = false;
          this.saveState();
        }
        this.trigger?.show();
      }, 15_000);
    }

    this.setupMessageBridge();

    if (!isInlineMode) {
      this.setupMobileDetection();
    }

    this.exposeGlobalAPI();

    if (isInlineMode) {
      this.isOpen = true;
    } else {
      this.restoreState();
    }
  }

  private onIframeLoad(): void {
    this.sendMessage({ type: 'config', payload: this.config });
  }

  private setupMessageBridge(): void {
    this.messageHandler = (event: MessageEvent) => {
      // Validate origin - only accept messages from our widget iframe
      const widgetOrigin = new URL(this.widgetUrl).origin;
      if (event.origin !== widgetOrigin && event.origin !== window.location.origin) {
        return;
      }

      const message = event.data as ChildToParentMessage;
      if (!message || typeof message.type !== 'string') return;

      this.handleChildMessage(message);
    };
    window.addEventListener('message', this.messageHandler);
  }

  private handleChildMessage(message: ChildToParentMessage): void {
    switch (message.type) {
      case 'ready':
        this.isReady = true;
        // Always send config first — the widget may have been reloaded by the
        // browser (mobile memory pressure, display:none optimisation) and lost
        // its state.  Sending config is idempotent so duplicates are harmless.
        this.iframe?.postMessage({ type: 'config', payload: this.config });
        // Flush any other pending messages (skip stale config duplicates)
        for (const msg of this.pendingMessages) {
          if (msg.type !== 'config') {
            this.iframe?.postMessage(msg);
          }
        }
        this.pendingMessages = [];
        // If widget should be open, tell it
        if (this.isOpen) {
          this.sendMessage({ type: 'open' });
        }
        break;

      case 'requestClose':
        this.close();
        break;

      case 'resize':
        if (this.iframe && message.payload) {
          const { width, height } = message.payload;
          if (width && height) {
            this.iframe.container.style.width = `${width}px`;
            this.iframe.container.style.height = `${height}px`;
          }
        }
        break;

      case 'loaded':
        // (a) pendingOpen → show widget, (b) !isOpen → reveal trigger, (c) else → no-op (re-emit)
        this.isRendered = true;
        if (this.pendingOpen) {
          this.pendingOpen = false;
          this.showWidget();
        } else if (!this.isOpen) {
          this.trigger?.show();
          if (this.popover) {
            this.popover.scheduleShow();
          }
        }
        break;

      case 'navigation':
        // User clicked a product/post card - set flag so widget stays closed after page nav
        this.setClosedByNavigation();
        break;
    }
  }

  private sendMessage(message: ParentToChildMessage): void {
    if (this.isReady && this.iframe) {
      this.iframe.postMessage(message);
    } else {
      this.pendingMessages.push(message);
    }
  }

  private setupMobileDetection(): void {
    // Apply once for the current viewport so the initial cssText is correct.
    const applyForCurrentViewport = (isMobile: boolean) => {
      if (this.iframe && this.isRendered) {
        applyMobileStyles(this.iframe, isMobile && this.isOpen);
      }
    };

    // `matchMedia('(max-width: 480px)').change` fires exactly when the
    // boolean we actually care about transitions — once when the user
    // crosses the breakpoint, not 60 times/sec while they drag the window
    // edge through it. Falls back to a raw `resize` listener only if
    // `matchMedia` is unsupported (effectively never in modern browsers,
    // but we don't control the host page's UA).
    if (typeof window.matchMedia === 'function') {
      const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
      this.mobileMql = mql;
      this.mobileMqlListener = (event: MediaQueryListEvent) => {
        applyForCurrentViewport(event.matches);
      };
      mql.addEventListener('change', this.mobileMqlListener);
      applyForCurrentViewport(mql.matches);
    } else {
      let lastIsMobile = window.innerWidth <= MOBILE_BREAKPOINT_PX;
      const fallback = () => {
        const isMobile = window.innerWidth <= MOBILE_BREAKPOINT_PX;
        if (isMobile === lastIsMobile) return;
        lastIsMobile = isMobile;
        applyForCurrentViewport(isMobile);
      };
      this.mobileMqlListener = fallback as unknown as (event: MediaQueryListEvent) => void;
      window.addEventListener('resize', fallback);
      applyForCurrentViewport(lastIsMobile);
    }

    // Some Safari builds don't fire `matchMedia.change` reliably across a
    // physical orientation flip until layout settles, so keep an explicit
    // orientationchange hook — but as a named handler that `destroy()`
    // can remove. The 100ms delay lets the visual viewport reflow before
    // we re-evaluate.
    this.orientationChangeListener = () => {
      window.setTimeout(() => {
        const isMobile = this.mobileMql ? this.mobileMql.matches : window.innerWidth <= MOBILE_BREAKPOINT_PX;
        applyForCurrentViewport(isMobile);
      }, 100);
    };
    window.addEventListener('orientationchange', this.orientationChangeListener);
  }

  private teardownMobileDetection(): void {
    if (this.mobileMql && this.mobileMqlListener) {
      this.mobileMql.removeEventListener('change', this.mobileMqlListener);
    } else if (this.mobileMqlListener) {
      window.removeEventListener('resize', this.mobileMqlListener as unknown as EventListener);
    }
    this.mobileMql = null;
    this.mobileMqlListener = null;

    if (this.orientationChangeListener) {
      window.removeEventListener('orientationchange', this.orientationChangeListener);
      this.orientationChangeListener = null;
    }
  }

  private restoreState(): void {
    try {
      const closedByNav = sessionStorage.getItem('savanto_widget_closed_by_navigation');
      if (closedByNav === 'true') {
        sessionStorage.removeItem('savanto_widget_closed_by_navigation');
        sessionStorage.removeItem('savanto_chat_open');
        return;
      }

      const savedState = sessionStorage.getItem('savanto_chat_open');
      if (savedState === 'true') {
        this.open();
      }
    } catch {
      // sessionStorage might not be available
    }
  }

  private saveState(): void {
    // Inline embeds are always open and must not write open/closed state that
    // a floating embed elsewhere on the same origin would then restore.
    if (this.config.embedMode === 'inline') return;
    try {
      sessionStorage.setItem('savanto_chat_open', String(this.isOpen));
    } catch {
      // sessionStorage might not be available
    }
  }

  private setClosedByNavigation(): void {
    // Inline embeds are always open and never navigate-away-and-restore; the
    // flag would only suppress a same-origin FLOATING embed's restore on the
    // next page — same isolation rule as `saveState` above.
    if (this.config.embedMode === 'inline') return;
    try {
      sessionStorage.setItem('savanto_widget_closed_by_navigation', 'true');
    } catch {
      // sessionStorage might not be available
    }
  }

  private syncPopover(newConfig: Partial<LoaderConfig>): void {
    // Inline embeds never show the welcome popover — a remote config with
    // popoverEnabled must not conjure one onto the host page.
    if (this.config.embedMode === 'inline') return;

    const popoverFieldChanged =
      newConfig.popoverEnabled !== undefined ||
      newConfig.popoverMessage !== undefined ||
      newConfig.popoverDelay !== undefined ||
      newConfig.triggerPosition !== undefined;

    if (!popoverFieldChanged) return;

    if (this.config.popoverEnabled === false) {
      this.popover?.cancelSchedule();
      this.popover?.hide();
      return;
    }

    if (this.popover) {
      this.popover.updateStyles(this.config);
    } else if (this.config.popoverEnabled && this.config.popoverMessage) {
      this.popover = createPopover(this.config, () => this.open());
      if (!this.isOpen && this.popover) {
        this.popover.scheduleShow();
      }
    }
  }

  private showWidget(): void {
    this.iframe?.show();
    this.sendMessage({ type: 'open' });

    // Apply mobile styles if needed. Read from the cached MQL when
    // available so this stays consistent with the breakpoint listener.
    const isMobile = this.mobileMql ? this.mobileMql.matches : window.innerWidth <= MOBILE_BREAKPOINT_PX;
    if (isMobile && this.iframe) {
      applyMobileStyles(this.iframe, true);
    }
  }

  // Public API methods

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.trigger?.hide();
    this.popover?.cancelSchedule();
    this.popover?.hide();
    this.saveState();

    if (this.isRendered) {
      this.showWidget();
    } else {
      this.pendingOpen = true;
    }
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.pendingOpen = false;
    this.sendMessage({ type: 'close' });
    this.saveState();

    if (this.iframe) {
      applyMobileStyles(this.iframe, false);
    }

    this.iframe?.hide(() => {
      if (this.isRendered) {
        this.trigger?.show();
      }
    });
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  ask(text: string): void {
    const trimmed = typeof text === 'string' ? text.trim() : '';
    if (!trimmed) return;
    this.open();
    // sendMessage queues until the iframe signals 'ready', so an ask fired
    // before the widget has rendered is delivered once it is.
    this.sendMessage({ type: 'ask', payload: { text: trimmed } });
  }

  isWidgetOpen(): boolean {
    return this.isOpen;
  }

  /**
   * Latch the server's branding requirement so later config writers
   * (`configure()`, `updateConfig`) can't turn it off.
   */
  lockBranding(): void {
    this.serverRequiresBranding = true;
    this.config.showBranding = true;
    this.config.brandingLocked = true;
  }

  getConfig(): LoaderConfig {
    return { ...this.config };
  }

  updateConfig(newConfig: Partial<LoaderConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // Branding is tier-enforced server-side and may only be forced ON by
    // the host page. Re-asserting at bootstrap alone left this write path
    // open: `SavantoChatbot.configure({ showBranding: false })` after load
    // would still strip it. Clamp here so every writer is covered.
    if (this.serverRequiresBranding) {
      this.config.showBranding = true;
      this.config.brandingLocked = true;
    }

    if (newConfig.theme || newConfig.triggerIconUrl || newConfig.triggerShowBackground !== undefined) {
      this.trigger?.updateStyles(this.config);
    }

    if (newConfig.triggerPosition && this.iframe) {
      this.iframe.updatePosition(newConfig.triggerPosition);
    }

    this.syncPopover(newConfig);

    this.sendMessage({ type: 'config', payload: this.config });
  }

  setTheme(theme: LoaderTheme): void {
    this.config.theme = { ...this.config.theme, ...theme };
    this.trigger?.updateStyles(this.config);
    this.popover?.updateStyles(this.config);
    this.sendMessage({ type: 'theme', payload: theme });
  }

  destroy(): void {
    this.teardownMobileDetection();
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    if (this.revealFallbackTimer != null) {
      clearTimeout(this.revealFallbackTimer);
      this.revealFallbackTimer = null;
    }
    this.trigger?.destroy();
    this.popover?.destroy();
    this.iframe?.destroy();
    this.trigger = null;
    this.popover = null;
    this.iframe = null;
    delete (window as unknown as Record<string, unknown>).SavantoChatbot;
  }

  // Analytics consent methods - send to iframe via postMessage
  grantAnalyticsConsent(): void {
    this.consentRevoked = false;
    this.consentExplicitlyGranted = true;
    this.sendMessage({ type: 'consent', payload: { analytics: 'granted' } } as ParentToChildMessage);
  }

  revokeAnalyticsConsent(): void {
    this.consentRevoked = true;
    this.consentExplicitlyGranted = false;
    this.sendMessage({ type: 'consent', payload: { analytics: 'revoked' } } as ParentToChildMessage);
  }

  getAnalyticsConsentStatus(): 'granted' | 'denied' | 'pending' {
    if (this.consentExplicitlyGranted) {
      return 'granted';
    }
    // Explicit revoke wins over implicit signals until grantAnalyticsConsent() runs again.
    if (this.consentRevoked) {
      return 'denied';
    }
    // If consent not required, it's implicitly granted
    if (!this.config.analyticsConsentRequired) {
      return 'granted';
    }
    // Check window.savantoConsent signal
    const consent = window.savantoConsent;
    if (consent === true || (typeof consent === 'object' && consent?.analytics === true)) {
      return 'granted';
    }
    if (consent === false || (typeof consent === 'object' && consent?.analytics === false)) {
      return 'denied';
    }
    return 'pending';
  }

  private exposeGlobalAPI(): void {
    const api: SavantoChatbotAPI = {
      open: () => this.open(),
      close: () => this.close(),
      toggle: () => this.toggle(),
      ask: (text: string) => this.ask(text),
      isOpen: () => this.isWidgetOpen(),
      getState: () => ({
        isOpen: this.isOpen,
        config: this.getConfig(),
      }),
      getConfig: () => this.getConfig(),
      configure: (config: Partial<LoaderConfig>) => {
        this.updateConfig(config);
        return true;
      },
      setTheme: (theme: LoaderTheme) => {
        this.setTheme(theme);
        return true;
      },
      setCustomCSS: (css: string) => {
        this.updateConfig({ customCSS: css });
        return true;
      },
      version: __VERSION__,
      isInitialized: () => true,
      destroy: () => this.destroy(),
      // Analytics consent methods (for GDPR compliance)
      grantAnalyticsConsent: () => this.grantAnalyticsConsent(),
      revokeAnalyticsConsent: () => this.revokeAnalyticsConsent(),
      getAnalyticsConsentStatus: () => this.getAnalyticsConsentStatus(),
    };

    (window as unknown as { SavantoChatbot: SavantoChatbotAPI }).SavantoChatbot = api;
  }
}

/**
 * Result of parsing data attributes from the script tag.
 */
interface DataAttributesResult {
  config: Partial<LoaderConfig>;
  /** True when the host opts into programmatic init via `data-manual`. */
  manualInit: boolean;
}

/**
 * Parse data attributes from the script tag
 * Supports single-tag embed: <script src="...loader.js" data-publishable-key="if_pk_xxx"></script>
 * Also recognises `data-manual` for opt-in programmatic init via SavantoChatbot.init({...}).
 */
function getConfigFromDataAttributes(): DataAttributesResult {
  // Find our script tag
  const script =
    document.currentScript ||
    document.querySelector('script[src*="savanto"][src*="loader"]') ||
    document.querySelector('script[src*="savanto"][data-manual]') ||
    document.querySelector('script[data-publishable-key]');

  if (!script) return { config: {}, manualInit: false };

  const config: Partial<LoaderConfig> = {};

  // Map data attributes to config properties
  const attrMap: Record<string, keyof LoaderConfig> = {
    // API
    'publishable-key': 'publishableKey',
    'workspace-id': 'workspaceId',
    'api-url': 'apiUrl',
    'widget-url': 'widgetUrl',
    // User authentication (for MCP operations like order lookup)
    'user-jwt': 'userJwt',
    // Embed mode
    'embed-mode': 'embedMode',
    container: 'containerSelector',
    // Position
    position: 'triggerPosition',
    // Branding
    title: 'title',
    subtitle: 'subtitle',
    greeting: 'greeting',
    placeholder: 'placeholder',
    avatar: 'avatarUrl',
    'show-branding': 'showBranding',
    // Trigger button
    'trigger-avatar': 'triggerIconUrl',
    'trigger-show-background': 'triggerShowBackground',
    'trigger-icon-show-border': 'triggerShowBorder',
    // Debug
    debug: 'debugMode',
  };

  // Boolean attributes
  const booleanAttrs = ['debugMode', 'showBranding', 'triggerShowBackground', 'triggerShowBorder'];

  for (const [attr, prop] of Object.entries(attrMap)) {
    const value = script.getAttribute(`data-${attr}`);
    if (value !== null) {
      if (booleanAttrs.includes(prop)) {
        // Handle booleans: data-debug or data-debug="true" → true, data-debug="false" → false
        (config as Record<string, unknown>)[prop] = value !== 'false' && (value === 'true' || value === '');
      } else {
        (config as Record<string, unknown>)[prop] = value;
      }
    }
  }

  // `data-manual` (presence-based boolean) suppresses auto-init in favour of
  // window.SavantoChatbot.init({...}).
  const manualAttr = script.getAttribute('data-manual');
  const manualInit = manualAttr !== null && manualAttr !== 'false';

  return { config, manualInit };
}

/**
 * Map legacy `prompts` (loose, undocumented loader-side shape) onto the
 * documented `initialPrompts: PromptData[]` field. Logs a one-time deprecation
 * warning when the legacy field is seen.
 */
function migrateLegacyPrompts(config: Partial<LoaderConfig>): void {
  const record = config as Record<string, unknown>;
  if (!('prompts' in record)) return;

  const legacy = record.prompts;
  delete record.prompts;

  if (config.initialPrompts !== undefined) {
    // Both supplied — initialPrompts wins; still warn so the host fixes their config.
    console.warn(
      '[Savanto] LoaderConfig.prompts is deprecated and was ignored because initialPrompts is also set. Remove the legacy `prompts` field.',
    );
    return;
  }

  if (!Array.isArray(legacy)) return;

  console.warn('[Savanto] LoaderConfig.prompts is deprecated; use `initialPrompts: PromptData[]` instead.');

  config.initialPrompts = legacy
    .map((item): PromptData | null => {
      if (typeof item !== 'object' || item === null) return null;
      const entry = item as Record<string, unknown>;
      const prompt =
        typeof entry.prompt === 'string' ? entry.prompt : typeof entry.text === 'string' ? entry.text : undefined;
      if (prompt === undefined) return null;
      const mapped: PromptData = { prompt };
      if (typeof entry.icon === 'string') mapped.icon = entry.icon;
      if (typeof entry.title === 'string') mapped.title = entry.title;
      return mapped;
    })
    .filter((p): p is PromptData => p !== null);
}

// Apply default config values if not set
function applyConfigDefaults(config: Partial<LoaderConfig>): LoaderConfig {
  const result = { ...config } as LoaderConfig;

  migrateLegacyPrompts(result);

  // Set default base API URL if not explicitly set
  if (result.publishableKey && !result.apiUrl) {
    result.apiUrl = 'https://api.savanto.ai';
  }

  // Set default widget URL if not explicitly set
  if (result.publishableKey && !result.widgetUrl) {
    result.widgetUrl = DEFAULT_WIDGET_URL;
  }

  // Default workspaceId from current origin if not specified
  if (!result.workspaceId) {
    // Convert URL to normalized workspace ID (e.g., "example.com" → "example-com")
    // Strip www. prefix to match workspace created during crawl
    const hostname = new URL(window.location.origin).hostname.replace(/^www\./, '');
    result.workspaceId = hostname.replace(/\./g, '-').toLowerCase();
  }

  return result;
}

/**
 * Result from fetching remote config
 */
interface FetchConfigResult {
  /** The config if successfully fetched, null otherwise */
  config: Partial<LoaderConfig> | null;
  /** Whether the widget is enabled — false for auth failures, trial expiration, or admin-disabled */
  enabled: boolean;
}

/**
 * Fetch widget configuration from the cloud API
 * Always fetches fresh config to ensure immediate updates when settings change.
 * Browser HTTP caching with ETag headers provides efficiency.
 */
async function fetchRemoteConfig(
  publishableKey: string,
  workspaceId: string,
  apiUrl: string,
): Promise<FetchConfigResult> {
  try {
    const response = await fetch(`${apiUrl}/config/widget/chat`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${publishableKey}`,
        'Content-Type': 'application/json',
        'X-Workspace-Id': workspaceId,
      },
    });

    // Any non-success response (auth failure, trial expired, server error) — don't show widget
    if (!response.ok) {
      return { config: null, enabled: false };
    }

    const result = await response.json();
    const remoteConfig = result.data;

    if (remoteConfig) {
      // Check if widget is explicitly disabled
      const enabled = remoteConfig.enabled !== false;

      // Direct pass-through - API field names match LoaderConfig
      const config: Partial<LoaderConfig> = {
        // Header
        title: remoteConfig.title,
        subtitle: remoteConfig.subtitle,
        avatarUrl: remoteConfig.avatarUrl,
        headerAvatarShowBackground: remoteConfig.headerAvatarShowBackground,
        headerAvatarShowBorder: remoteConfig.headerAvatarShowBorder,
        // Greeting / Empty State
        greeting: remoteConfig.greeting,
        greetingSubtext: remoteConfig.greetingSubtext,
        greetingIcon: remoteConfig.greetingIconUrl,
        placeholder: remoteConfig.placeholder,
        triggerPosition: remoteConfig.triggerPosition,
        triggerIconUrl: remoteConfig.triggerIconUrl,
        triggerShowBackground: remoteConfig.triggerShowBackground,
        triggerShowBorder: remoteConfig.triggerShowBorder,
        cardLayout: remoteConfig.cardLayout,
        productCardVariant: remoteConfig.productCardVariant,
        promptStyle: remoteConfig.promptStyle,
        productCardImageDisplay: remoteConfig.productCardImageDisplay,
        productCardImageAspectRatio: remoteConfig.productCardImageAspectRatio,
        postCardImageDisplay: remoteConfig.postCardImageDisplay,
        postCardImageAspectRatio: remoteConfig.postCardImageAspectRatio,
        currency: remoteConfig.currency,
        showMessageActions: remoteConfig.showMessageActions,
        showUserCopyButton: remoteConfig.showUserCopyButton,
        showMessageTimestamps: remoteConfig.showMessageTimestamps,
        showPostImage: remoteConfig.showPostImage,
        showPostAuthor: remoteConfig.showPostAuthor,
        showPostDate: remoteConfig.showPostDate,
        showPostCategory: remoteConfig.showPostCategory,
        // Theme & Styling
        customCSS: remoteConfig.customCSS,
        theme: remoteConfig.theme,
        darkTheme: remoteConfig.darkTheme,
        colorScheme: remoteConfig.colorScheme ?? remoteConfig.theme?.colorScheme ?? remoteConfig.darkTheme?.colorScheme,
        // Live Agent Configuration
        liveAgentEnabled: remoteConfig.liveAgentEnabled,
        liveAgentConfigured: remoteConfig.liveAgentConfigured,
        liveAgentManualHandoff: remoteConfig.liveAgentManualHandoff,
        liveAgentAutoHandoff: remoteConfig.liveAgentAutoHandoff,
        liveAgentProvider: remoteConfig.liveAgentProvider,
        liveAgentBusinessHours: remoteConfig.liveAgentBusinessHours,
        liveAgentInactivityTimeout: remoteConfig.liveAgentInactivityTimeout,
        liveAgentEscalationPrompt: remoteConfig.liveAgentEscalationPrompt,
        contactFormEnabled: remoteConfig.contactFormEnabled,
        // Welcome Popover
        popoverEnabled: remoteConfig.popoverEnabled,
        popoverMessage: remoteConfig.popoverMessage,
        popoverDelay: remoteConfig.popoverDelay,
        popoverShowOnce: remoteConfig.popoverShowOnce,
        // Branding
        showBranding: remoteConfig.showBranding,
      };

      return { config, enabled };
    }

    return { config: null, enabled: false };
  } catch {
    // Network errors - fail closed, don't show widget without server validation
    return { config: null, enabled: false };
  }
}

/**
 * Bootstrap the loader from a fully merged local config + the original
 * data-attr config (so remote-config can still be overridden by data attrs).
 *
 * Shared by auto-init, the Shopify `savanto:settings-loaded` deferred path,
 * and programmatic `SavantoChatbot.init(...)`.
 */
async function bootstrap(localConfig: Partial<LoaderConfig>, dataAttrConfig: Partial<LoaderConfig>): Promise<boolean> {
  const mergedConfig = { ...localConfig, ...dataAttrConfig };
  let config = applyConfigDefaults(mergedConfig);
  let serverRequiresBranding = false;

  if (!config.publishableKey) {
    console.warn('[Savanto] Widget not initialized: missing publishable key');
    return false;
  }

  if (!config.workspaceId) {
    console.warn('[Savanto] Widget not initialized: missing workspace ID');
    return false;
  }

  // Fetch remote config to validate auth and check enabled status
  if (config.apiUrl) {
    const result = await fetchRemoteConfig(config.publishableKey, config.workspaceId, config.apiUrl);

    if (!result.enabled) {
      return false;
    }

    if (result.config) {
      // Remote config is base, local config overrides
      config = applyConfigDefaults({
        ...result.config,
        ...mergedConfig,
      });

      // Branding is tier-enforced server-side (free/personal must show
      // "Powered by Savanto") — it is the acquisition channel that pays
      // for those tiers. Local overrides (data attrs, window config) may
      // only force branding ON, never off; without this re-assert a
      // site owner could strip it with data-show-branding="false".
      if (result.config.showBranding) {
        config.showBranding = true;
        // Explicit, server-sourced flag carried into the iframe so the
        // widget can distinguish "required" from "merely on".
        config.brandingLocked = true;
        serverRequiresBranding = true;
      }
    }
  }

  const loader = new SavantoLoader(config);
  if (serverRequiresBranding) loader.lockBranding();
  return true;
}

/**
 * Expose a minimal pre-init stub on `window.SavantoChatbot` so the host page
 * can call `SavantoChatbot.init({...})` once it's ready. After init runs, the
 * full API exposed by `SavantoLoader` replaces this stub.
 */
function exposeInitStub(dataAttrConfig: Partial<LoaderConfig>): void {
  let alreadyInitialized = false;
  let initializing = false;
  const stub = {
    init: (userConfig: Partial<LoaderConfig> = {}) => {
      if (alreadyInitialized || initializing) {
        console.warn('[Savanto] SavantoChatbot.init() already called; ignoring repeat invocation.');
        return;
      }
      initializing = true;
      void bootstrap(userConfig, dataAttrConfig).then((ok) => {
        initializing = false;
        if (ok) {
          alreadyInitialized = true;
        }
      });
    },
    isInitialized: () => alreadyInitialized,
    version: __VERSION__,
  };
  // Stub doesn't implement the full SavantoChatbotAPI yet — that happens after
  // bootstrap. Cast through unknown to avoid a structurally invalid assignment.
  (window as unknown as { SavantoChatbot: Partial<SavantoChatbotAPI> }).SavantoChatbot =
    stub as unknown as Partial<SavantoChatbotAPI>;
}

// Auto-initialize when script loads
async function initLoader(): Promise<void> {
  // Get config from data attributes (single-tag embed)
  const { config: dataAttrConfig, manualInit } = getConfigFromDataAttributes();

  // `data-manual` → expose a stub global and wait for SavantoChatbot.init({...}).
  if (manualInit) {
    exposeInitStub(dataAttrConfig);
    return;
  }

  // Merge: data attributes override window.savantoConfig. Defaults are applied in bootstrap()
  // so legacy-field migration (and its one-time console warning) only runs once.
  const localConfig = window.savantoConfig ?? {};
  const mergedConfig = { ...localConfig, ...dataAttrConfig };

  // Check if settings are still loading (Shopify context)
  const settingsLoading = '_settingsLoaded' in mergedConfig && !mergedConfig._settingsLoaded;

  if (settingsLoading) {
    // Wait for settings to load
    window.addEventListener('savanto:settings-loaded', async (event: Event) => {
      const customEvent = event as CustomEvent;
      await bootstrap(customEvent.detail?.config ?? {}, dataAttrConfig);
    });
    return;
  }

  await bootstrap(localConfig, dataAttrConfig);
}

// Initialize
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLoader);
  } else {
    initLoader();
  }
}

export type { LoaderConfig, LoaderTheme, SavantoChatbotAPI };
export { SavantoLoader };
